import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { OrderCreationLayout } from '@/components/order/OrderCreationLayout'
import { SurveySectionHeader } from '@/components/order/SurveySectionHeader'
import { OrderQuestionRenderer } from '@/components/order/OrderQuestionRenderer'
import {
  CheckCircle2,
  AlertCircle,
  FileQuestion,
  Loader2,
  Circle,
  ChevronRight,
} from 'lucide-react'
import {
  getEvaluationSnapshot,
  createEvaluationResponse,
  updateEvaluationResponse,
  submitEvaluationResponse,
  uploadEvaluationFile,
} from '@/services/orderApi'
import { useOrderCreationStore } from '@/store/orderCreationStore'
import { useLocalization } from '@/contexts/LocalizationContext'
import { toast } from 'sonner'
import {
  transformEvaluationSnapshot,
  mapAnswersToCodes,
  getVisibleQuestions,
  serializeAnswersForApi,
  hasQuestions,
  type SurveyConfig,
  type TransformedQuestion,
} from '@/utils/evaluationSnapshotTransformer'
import {
  isValidRegexPattern,
  createSafeRegExp,
  getRegexValidationMessage,
} from '@/utils/validation'
import { cn } from '@/lib/utils'

/** Dynamic consent question code from survey templates (remote consultation & privacy). */
const CONSENT_QUESTION_CODE = 'CONSENT1'

// ---------------------------------------------------------------------------
// Section builder — groups visible questions into renderable sections
// ---------------------------------------------------------------------------

interface QuestionSection {
  header: TransformedQuestion | null
  questions: TransformedQuestion[]
}

function buildQuestionSections(
  visibleQuestions: TransformedQuestion[],
): QuestionSection[] {
  const sections: QuestionSection[] = []
  let current: QuestionSection = { header: null, questions: [] }

  for (const q of visibleQuestions) {
    const isSectionStart =
      q.appointment_section_number != null || q.section_type != null

    if (isSectionStart && current.questions.length > 0) {
      // Close current section, start a new one
      sections.push(current)
      current = { header: q, questions: [q] }
    } else if (isSectionStart) {
      // First question is a section start
      current = { header: q, questions: [q] }
    } else {
      current.questions.push(q)
    }
  }

  if (current.questions.length > 0) {
    sections.push(current)
  }

  return sections
}

// ---------------------------------------------------------------------------
// QuestionsPage component
// ---------------------------------------------------------------------------

export function QuestionsPage() {
  const navigate = useNavigate()
  const { locale, t } = useLocalization()
  const isArabic = locale === 'ar'
  const {
    orderId,
    orderNumber,
    evaluationSnapshotId,
    evaluationSnapshot,
    setEvaluationSnapshot,
    evaluationResponseId,
    setEvaluationResponseId,
    setCurrentStep,
    appointments,
  } = useOrderCreationStore()

  const [surveyConfig, setSurveyConfig] = useState<SurveyConfig | null>(null)
  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // -----------------------------------------------------------------------
  // Derived data: visible questions, sections, progress
  // -----------------------------------------------------------------------

  /** Visible questions after conditional-logic filtering. */
  const visibleQuestions = useMemo(() => {
    if (!surveyConfig) return []
    return getVisibleQuestions(surveyConfig.questions, answers)
  }, [surveyConfig, answers])

  /** Sections built from visible questions for grouped rendering. */
  const questionSections = useMemo(
    () => buildQuestionSections(visibleQuestions),
    [visibleQuestions],
  )

  /**
   * Progress & validation.
   * - Display counts use visibleQuestions (what user sees).
   * - Required validation uses full surveyConfig.questions so hidden-but-
   *   required questions are still enforced.
   */
  const {
    answeredCount,
    requiredCount,
    visibleCount,
    progress,
    allRequiredAnswered,
  } = useMemo(() => {
    if (!surveyConfig)
      return {
        answeredCount: 0,
        requiredCount: 0,
        visibleCount: 0,
        progress: 0,
        allRequiredAnswered: true,
      }

    const allQuestions = surveyConfig.questions
    const requiredQuestions = allQuestions.filter(
      (q) => q.validation_rules.required,
    )
    const requiredCount = requiredQuestions.length

    // Answered = visible questions that have a non-empty answer
    const answeredCount = visibleQuestions.filter((q) =>
      isAnsweredValue(answers[q.id]),
    ).length

    const visibleCount = visibleQuestions.length

    // Validation: every required question (visible or not) must be answered,
    // AND no question may have a regex-invalid answer
    const allRequiredAnswered = requiredQuestions.every((q) =>
      isAnsweredValue(answers[q.id]),
    )

    const hasAnyRegexError = allQuestions.some((q) => {
      const pattern = q.validation_rules.pattern
      if (!isValidRegexPattern(pattern)) return false
      const val = answers[q.id]
      if (!isAnsweredValue(val)) return false
      try {
        return !createSafeRegExp(pattern).test(String(val))
      } catch {
        return false
      }
    })

    const consentQuestions = visibleQuestions.filter(
      (q) => q.code === CONSENT_QUESTION_CODE,
    )
    const consentAnswersOk =
      consentQuestions.length === 0 ||
      consentQuestions.every((q) =>
        isConsentAnswerAffirmative(q, answers[q.id]),
      )

    const progress =
      visibleCount > 0
        ? Math.round((answeredCount / visibleCount) * 100)
        : 0

    return {
      answeredCount,
      requiredCount,
      visibleCount,
      progress,
      allRequiredAnswered:
        allRequiredAnswered && !hasAnyRegexError && consentAnswersOk,
    }
  }, [surveyConfig, visibleQuestions, answers])

  // -----------------------------------------------------------------------
  // Load survey
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!orderId || !evaluationSnapshotId) {
      navigate('/create-order/beneficiary')
      return
    }

    loadSurvey()

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [orderId, evaluationSnapshotId, navigate])

  const loadSurvey = async () => {
    if (!orderId) return

    try {
      setLoading(true)
      setError(null)

      // Get snapshot if not already loaded
      let snapshot = evaluationSnapshot
      if (!snapshot) {
        snapshot = await getEvaluationSnapshot(orderId)
        console.log('[QuestionsPage] Evaluation snapshot received:', snapshot)
        setEvaluationSnapshot(snapshot)
      }

      if (!snapshot) {
        console.log(
          '[QuestionsPage] No evaluation snapshot found, skipping to payment',
        )
        navigate('/create-order/payment')
        return
      }

      // Check if snapshot has questions
      if (!hasQuestions(snapshot)) {
        console.log(
          '[QuestionsPage] No questions in evaluation snapshot, skipping to payment',
        )
        navigate('/create-order/payment')
        return
      }

      // Transform snapshot to survey config — pass appointments for section metadata
      const config = transformEvaluationSnapshot(snapshot, appointments)

      console.log('[QuestionsPage] Transformed survey config:', {
        questionsCount: config.questions.length,
        questions: config.questions.map((q) => ({
          id: q.id,
          text_en: q.text_translations.en,
          type: q.question_type,
          hasOptions: q.options && q.options.length > 0,
          required: q.validation_rules.required,
          section_type: q.section_type,
          appointment_section_number: q.appointment_section_number,
        })),
      })

      // Double-check that we have questions after transformation
      if (!config.questions || config.questions.length === 0) {
        console.log(
          '[QuestionsPage] No questions after transformation, skipping to payment',
        )
        navigate('/create-order/payment')
        return
      }

      setSurveyConfig(config)

      // Create evaluation response
      const response = await createEvaluationResponse({
        aggregated_survey_snapshot: evaluationSnapshotId!,
        object_id: orderId,
        content_type: 'order',
        answers: {},
      })
      setEvaluationResponseId(response.id)
    } catch (err: any) {
      if (
        err.message?.includes('No evaluation snapshot') ||
        err.message?.includes('No questions')
      ) {
        console.log(
          '[QuestionsPage] No questions available, skipping to payment',
        )
        navigate('/create-order/payment')
        return
      }

      setError(err.message || t('orderCreation.failedToLoadForm'))
      console.error('[QuestionsPage] Error loading survey:', err)
    } finally {
      setLoading(false)
    }
  }

  // -----------------------------------------------------------------------
  // Auto-save & submit — serialize location answers before sending
  // -----------------------------------------------------------------------

  const debouncedSave = useCallback(
    async (updatedAnswers: Record<string, any>) => {
      if (!evaluationResponseId || !surveyConfig) return

      setSaving(true)
      try {
        const serialized = serializeAnswersForApi(updatedAnswers, surveyConfig)
        const mappedAnswers = mapAnswersToCodes(
          serialized,
          surveyConfig.questions,
        )
        await updateEvaluationResponse(evaluationResponseId, mappedAnswers)
      } catch (err) {
        console.error('[QuestionsPage] Auto-save failed:', err)
      } finally {
        setSaving(false)
      }
    },
    [evaluationResponseId, surveyConfig],
  )

  const handleAnswerChange = useCallback(
    (questionId: string, value: any) => {
      const newAnswers = { ...answers, [questionId]: value }
      setAnswers(newAnswers)
      setTouchedFields((prev) => new Set(prev).add(questionId))

      // Clear previous timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      // Debounce auto-save
      saveTimeoutRef.current = setTimeout(() => {
        debouncedSave(newAnswers)
      }, 1000)
    },
    [answers, debouncedSave],
  )

  const evaluationUploadFn = useCallback(
    (file: File, questionCode: string, onProgress?: (p: { loaded: number; total: number; percentage: number }) => void) => {
      if (!evaluationResponseId) {
        return Promise.reject(new Error('Evaluation response not yet created'))
      }
      return uploadEvaluationFile(evaluationResponseId, file, questionCode, onProgress)
    },
    [evaluationResponseId],
  )

  const handleSubmit = async () => {
    if (!evaluationResponseId || !surveyConfig) return

    // Mark all fields as touched to show validation
    const allIds = new Set(surveyConfig.questions.map((q) => q.id))
    setTouchedFields(allIds)

    if (!allRequiredAnswered) {
      // Find first problematic question: either unanswered required, or regex-invalid
      const firstInvalid = visibleQuestions.find(
        (q) =>
          (q.validation_rules.required && !isAnsweredValue(answers[q.id])) ||
          (isValidRegexPattern(q.validation_rules.pattern) &&
            isAnsweredValue(answers[q.id]) &&
            (() => {
              try {
                return !createSafeRegExp(q.validation_rules.pattern!).test(String(answers[q.id]))
              } catch {
                return false
              }
            })()) ||
          (q.code === CONSENT_QUESTION_CODE &&
            !isConsentAnswerAffirmative(q, answers[q.id])),
      )

      const message =
        firstInvalid?.code === CONSENT_QUESTION_CODE &&
        !isConsentAnswerAffirmative(firstInvalid, answers[firstInvalid.id])
          ? t('orderCreation.consentRequiredToContinue')
          : firstInvalid && isAnsweredValue(answers[firstInvalid.id])
            ? isArabic
              ? 'يرجى تصحيح الحقول غير الصحيحة'
              : 'Please fix invalid fields'
            : t('orderCreation.pleaseAnswerAllRequired')

      setError(message)
      toast.warning(message, {
        description: t('orderCreation.answerAllRequiredToContinue'),
      })

      if (firstInvalid) {
        setTimeout(() => {
          const el = document.getElementById(`question-${firstInvalid.id}`)
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 150)
      }
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      const serialized = serializeAnswersForApi(answers, surveyConfig)
      const mappedAnswers = mapAnswersToCodes(
        serialized,
        surveyConfig.questions,
      )
      await submitEvaluationResponse(evaluationResponseId, mappedAnswers)
      setSubmitted(true)

      // Navigate to payment after a short delay
      setTimeout(() => {
        setCurrentStep(7)
        navigate('/create-order/payment')
      }, 1500)
    } catch (err: any) {
      setError(err.message || t('orderCreation.failedToSubmitAnswers'))
      console.error('[QuestionsPage] Error submitting answers:', err)
    } finally {
      setSubmitting(false)
    }
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  const isAnswered = (questionId: string) => isAnsweredValue(answers[questionId])

  const hasRegexError = (question: TransformedQuestion): boolean => {
    const pattern = question.validation_rules.pattern
    if (!isValidRegexPattern(pattern)) return false
    const val = answers[question.id]
    if (!isAnsweredValue(val)) return false
    try {
      return !createSafeRegExp(pattern).test(String(val))
    } catch {
      return false
    }
  }

  const hasError = (question: TransformedQuestion) => {
    if (!touchedFields.has(question.id)) return false
    if (
      question.code === CONSENT_QUESTION_CODE &&
      !isConsentAnswerAffirmative(question, answers[question.id])
    ) {
      return true
    }
    if (question.validation_rules.required && !isAnswered(question.id)) return true
    return hasRegexError(question)
  }

  const getErrorMessage = (question: TransformedQuestion): string | undefined => {
    if (!touchedFields.has(question.id)) return undefined
    if (
      question.code === CONSENT_QUESTION_CODE &&
      !isConsentAnswerAffirmative(question, answers[question.id])
    ) {
      return t('orderCreation.consentRequiredToContinue')
    }
    if (question.validation_rules.required && !isAnswered(question.id)) {
      return t('orderCreation.questionRequired')
    }
    if (hasRegexError(question)) {
      return getRegexValidationMessage(question.validation_rules.pattern!, isArabic)
    }
    return undefined
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          <Card className="mb-6">
            <CardContent className="py-4">
              <Skeleton className="h-6 w-1/2 mb-2" />
              <Skeleton className="h-4 w-1/3" />
            </CardContent>
          </Card>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )
    }

    if (error && !surveyConfig) {
      return (
        <Card className="border-destructive">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>{t('common.error')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadSurvey}>{t('common.retry')}</Button>
          </CardContent>
        </Card>
      )
    }

    if (submitted) {
      return (
        <Card className="border-primary bg-primary/5">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-primary" />
            <CardTitle className="text-2xl mb-2">
              {t('orderCreation.answersSubmitted')}
            </CardTitle>
            <CardDescription>
              {t('orderCreation.redirectingToPayment')}
            </CardDescription>
          </CardContent>
        </Card>
      )
    }

    if (!surveyConfig) {
      return null
    }

    // Build a global question number map: question id → 1-based index
    // across all visible questions (so numbering is continuous across sections)
    const questionNumberMap = new Map<string, number>()
    visibleQuestions.forEach((q, i) => questionNumberMap.set(q.id, i + 1))

    return (
      <>
        {/* Survey Header with Progress */}
        <Card className="mb-6 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="py-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileQuestion className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">
                    {isArabic
                      ? surveyConfig.name_translations.ar
                      : surveyConfig.name_translations.en}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t('orderCreation.orderNumber', {
                      number: orderNumber ?? '',
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {saving && (
                  <Badge variant="outline" className="gap-1 animate-pulse">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t('common.saving')}
                  </Badge>
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('orderCreation.questionsAnswered', {
                    answered: answeredCount,
                    total: visibleCount,
                  })}
                </span>
                <span className="font-medium text-primary">{progress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-primary h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {requiredCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('orderCreation.requiredQuestions', {
                    count: requiredCount,
                  })}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Error message if any */}
        {error && (
          <Card className="mb-4 border-destructive bg-destructive/5">
            <CardContent className="py-3">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questions — rendered as sections */}
        <div className="space-y-4 mb-6">
          {questionSections.map((section, sectionIndex) => {
            // Determine if section contains any answered or errored questions
            const sectionHasAnswered = section.questions.some((q) =>
              isAnswered(q.id),
            )
            const sectionHasError = section.questions.some((q) => hasError(q))

            return (
              <Card
                key={section.questions[0]?.id ?? sectionIndex}
                className={cn(
                  'transition-all duration-200',
                  sectionHasAnswered && 'border-primary/30 bg-primary/[0.02]',
                  sectionHasError && 'border-destructive',
                )}
              >
                <CardContent className="p-6">
                  {/* Section header (only when section metadata is present) */}
                  {section.header != null &&
                    (section.header.section_type != null ||
                      section.header.appointment_section_number != null) && (
                      <SurveySectionHeader
                        appointmentSectionNumber={
                          section.header.appointment_section_number
                        }
                        sectionType={section.header.section_type}
                        sectionTitleTranslations={
                          section.header.section_title_translations
                        }
                        appointmentScheduledAt={
                          section.header.appointment_scheduled_at
                        }
                        isArabic={isArabic}
                      />
                    )}

                  {/* Questions within section */}
                  <div className="space-y-6">
                    {section.questions.map((question) => (
                      <div
                        key={question.id}
                        id={`question-${question.id}`}
                        className="scroll-mt-24"
                      >
                        <OrderQuestionRenderer
                          question={question}
                          value={answers[question.id]}
                          onChange={handleAnswerChange}
                          isArabic={isArabic}
                          hasError={hasError(question)}
                          isAnswered={isAnswered(question.id)}
                          questionNumber={questionNumberMap.get(question.id)}
                          showQuestionHeader={true}
                          uploadFn={evaluationResponseId ? evaluationUploadFn : undefined}
                          errorMessage={getErrorMessage(question)}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Submit button */}
        <Card className="sticky bottom-4 shadow-lg border-2">
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {allRequiredAnswered ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="text-primary font-medium">
                      {t('orderCreation.allRequiredAnswered')}
                    </span>
                  </>
                ) : (
                  <>
                    <Circle className="h-5 w-5" />
                    <span>
                      {t('orderCreation.answerAllRequiredToContinue')}
                    </span>
                  </>
                )}
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting}
                size="lg"
                className="min-w-[160px] gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('common.submitting')}
                  </>
                ) : (
                  <>
                    {t('orderCreation.continueToPayment')}
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </>
    )
  }

  return (
    <OrderCreationLayout
      titleKey="orderCreation.assessmentQuestions"
      descriptionKey="orderCreation.assessmentQuestionsDescription"
      step={6}
      backPath="/create-order/beneficiary"
    >
      <div className="max-w-3xl mx-auto pb-8">{renderContent()}</div>
    </OrderCreationLayout>
  )
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function isAnsweredValue(answer: any): boolean {
  return (
    answer !== undefined &&
    answer !== '' &&
    answer !== null &&
    !(Array.isArray(answer) && answer.length === 0)
  )
}

/**
 * True when the answer counts as agreement for CONSENT1-style questions.
 * Only used when a visible question has code CONSENT1.
 */
function isConsentAnswerAffirmative(
  question: TransformedQuestion,
  value: any,
): boolean {
  if (value === undefined || value === null || value === '') return false
  if (value === false || value === 'no') return false

  const ty = question.question_type

  if (ty === 'yes_no' || ty === 'yes_no_conditional') {
    if (value === true || value === 'yes') return true
    if (typeof value === 'object' && value !== null && value.choice === 'yes')
      return true
    return false
  }

  if (ty === 'multiple_choice' && Array.isArray(value)) {
    if (value.length === 0) return false
    return value.every((v) => choiceValueAffirms(question, v))
  }

  if (
    ty === 'single_choice' ||
    ty === 'dropdown' ||
    ty === 'radio' ||
    ty === 'multiple_choice'
  ) {
    return choiceValueAffirms(question, value)
  }

  if (value === true) return true
  return stringLooksAffirmative(String(value))
}

const AFFIRMATIVE_TOKENS = new Set([
  'yes',
  'true',
  'agreed',
  'agree',
  '1',
  'accept',
  'accepted',
])

function stringLooksAffirmative(s: string): boolean {
  const t = s.trim().toLowerCase()
  return AFFIRMATIVE_TOKENS.has(t)
}

function optionLooksAffirmative(
  opt: NonNullable<TransformedQuestion['options']>[number],
): boolean {
  const valueLower = String(opt.value).trim().toLowerCase()
  const idLower = String(opt.id).trim().toLowerCase()
  if (AFFIRMATIVE_TOKENS.has(valueLower) || AFFIRMATIVE_TOKENS.has(idLower))
    return true

  const en = opt.label_translations.en.toLowerCase()
  const ar = opt.label_translations.ar.toLowerCase()
  const blob = `${en} ${ar}`
  if (
    /\b(no|disagree|do not agree|reject|decline|don't agree|dont agree)\b/i.test(
      blob,
    )
  ) {
    return false
  }
  if (
    /\b(yes|agree|agreed|accepted|accept|consent|موافق|أوافق|نعم)\b/i.test(blob)
  ) {
    return true
  }
  return false
}

function choiceValueAffirms(question: TransformedQuestion, value: any): boolean {
  if (value === true) return true
  if (value === false || value === 'no') return false
  if (stringLooksAffirmative(String(value))) return true

  const opt = question.options?.find(
    (o) => o.value === String(value) || o.id === String(value),
  )
  if (opt) return optionLooksAffirmative(opt)
  return false
}
